import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Inbox, Search, Filter, ArrowRight, Eye, CheckCircle2, Clock, AlertCircle, FileText } from 'lucide-react';
import { ENQUIRY_STATUS_CONFIG, EnquiryStatus } from '@ftt/shared';

export const AdminEnquiriesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');

  const { data: enquiriesRes, isLoading } = useQuery({
    queryKey: ['admin-enquiries', { status: selectedStatus, search: searchTerm }],
    queryFn: () => {
      let url = '/enquiries';
      const params = new URLSearchParams();
      if (selectedStatus !== 'ALL') params.append('status', selectedStatus);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const enquiries = enquiriesRes || [];

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              ACQUISITIONS & INTAKE
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Inbox className="w-7 h-7 text-[#FF6B4A]" />
            <span>Publishing Enquiries &amp; Proposals</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Review incoming author submissions, assign acquisitions staff, and convert into active publishing projects.
          </p>
        </div>

        <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono font-bold text-slate-700 shrink-0">
          <FileText className="w-4 h-4 text-brand-600" />
          <span>Submissions: <strong className="text-slate-900">{enquiries.length}</strong></span>
        </div>
      </div>

      {/* 2. Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by ref no, title, author, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:border-brand-500 transition-colors"
          />
        </div>

        <div className="flex flex-wrap gap-1.5 text-xs">
          {['ALL', 'NEW', 'UNDER_REVIEW', 'CONSULTATION', 'PROPOSAL_SENT', 'CONVERTED', 'DECLINED'].map((st) => (
            <button
              key={st}
              onClick={() => setSelectedStatus(st)}
              className={`px-3 py-1.5 rounded-xl font-bold transition-colors cursor-pointer text-xs ${
                selectedStatus === st
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
              }`}
            >
              {st === 'ALL' ? 'All' : st.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Enquiries Table */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">Loading enquiries...</div>
      ) : enquiries.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <Inbox className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-900">No Enquiries Found</h3>
          <p className="text-xs text-slate-500">No submissions match your current search and filter criteria.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200/90 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50/80 text-slate-500 font-mono text-[11px] uppercase border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-bold">Reference No</th>
                  <th className="px-6 py-4 font-bold">Book Title &amp; Genre</th>
                  <th className="px-6 py-4 font-bold">Author / Client</th>
                  <th className="px-6 py-4 font-bold">Project Stage</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                  <th className="px-6 py-4 font-bold">Submitted</th>
                  <th className="px-6 py-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {enquiries.map((enq: any) => {
                  const statusConf = ENQUIRY_STATUS_CONFIG[enq.status as EnquiryStatus] || { label: enq.status, color: 'blue' };
                  return (
                    <tr key={enq.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-6 py-4 font-mono font-bold text-brand-700">
                        {enq.referenceNo}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900">{enq.bookTitle}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{enq.genre}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-800">{enq.fullName}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{enq.email}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-mono text-[11px]">
                        {enq.stage.replace(/_/g, ' ')}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold uppercase ${
                          enq.status === 'CONVERTED'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : enq.status === 'NEW'
                            ? 'bg-brand-50 text-brand-700 border border-brand-200'
                            : 'bg-slate-100 text-slate-700 border border-slate-200'
                        }`}>
                          {statusConf.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-500 font-mono text-[11px]">
                        {new Date(enq.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/admin/enquiries/${enq.id}`}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-colors shadow-xs"
                        >
                          <Eye className="w-3.5 h-3.5 text-[#FF6B4A]" />
                          <span>Review</span>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};
